/* eslint-disable default-case */
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { AlertHeader, Button, H3, Message, ModalFactory, ModalFooter, ModalTitle as ModalTitleBase, Switch, Text } from 'components';
import { lineHeight, theme, toRem } from 'design';
import { Appearance } from 'design/types';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { CalculatorRules, generateCalculatorRulesRequest, generateSalesGroupsRequest, selectCalculatorRules, updateCalculatorRulesRequest } from 'store/CalculatorRules';
import { IStore } from 'store/types';
import { ICategoryGroup, ICreateUpdateCalculatorRules, TipCalculatorMethod } from 'types';
import { sortAlphaNumerically } from 'utils';
import { LoadingText as LoadingTextBase, ModalContent, StyledSpinner } from '../../shared/styles';
import { SalesGroupItem } from '../SalesGroups/SalesGroupItem';
import { TipShareItem } from '../TipCalculations/TipShareItem';

interface IAIRulesModalProps {
  siteId: string;
  onClose(): void;
  dataCy?: string;
}

const Content = styled(ModalContent)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${toRem(theme.space.lg)};

  p.center {
    text-align: center;
  }
`;

const RulesLayout = styled.section`
  flex-direction: column;
  width: 100%;
  gap: ${toRem(theme.space.sm)};
`;

const SalesGroupsContainer = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${toRem(theme.space.sm)};
  width: 100%;
`;

const SwitchContainer = styled.div`
  background-color: ${theme.color.neutral[100]};
  border: 1px solid ${theme.color.neutral[200]};
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 93.5px;
  padding: ${toRem(theme.space.xl)};
  border-radius: 5.5px;
  gap: ${toRem(theme.space.sm)};
`;

const LabelContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const ItemLabel = styled.label`
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.medium};
  font-size: ${toRem(theme.font.size.md)};
  line-height: ${lineHeight(theme.font.size.md)};
  color: ${theme.color.neutral[800]};
`;

const ItemDescription = styled.p`
  font-family: ${theme.font.family.euclid};
  color: ${theme.color.neutral[600]};
  font-size: ${toRem(theme.font.size.xs)};
  line-height: ${lineHeight(theme.font.size.xs)};
`;

const ModalTitle = styled(ModalTitleBase)`
  font-size: ${toRem(theme.font.size.lg)};
  line-height: ${lineHeight(theme.font.size.lg)};
  margin-bottom: ${toRem(theme.space.sm)};
`;

const HeaderContainer = styled.div`
  display: block;
`;

const ContinueButton = styled(Button)`
  width: 100%;
  margin-top: ${toRem(theme.space.sm)};
`;

const ReviewHeader = styled(H3)`
  width: 100%;
`;

const ErrorText = styled(Text)`
  text-align: center;
`;

const DisclaimerText = styled.p`
  font-family: ${theme.font.family.euclid};
  color: ${theme.color.neutral[600]};
  font-size: ${toRem(theme.font.size.xs)};
  line-height: ${lineHeight(theme.font.size.xs)};
`;

const LoadingText = styled(LoadingTextBase)`
  font-size: ${toRem(theme.font.size.md)};
  line-height: ${lineHeight(theme.font.size.md)};
`;

type ModalType = 'generate' | 'result';
type SwitchType = 'sales-groups' | 'rules';

export const AIRulesModal: React.FC<IAIRulesModalProps> = ({ siteId, onClose, dataCy = 'ai-rules-modal' }) => {
  const [modal, setModal] = useState<ModalType>('generate');
  const [switchState, setSwitchState] = useState(new Set<SwitchType>(['sales-groups']));
  const dispatch = useDispatch();
  const [rules, setRules] = useState<ICreateUpdateCalculatorRules>(null);
  const [salesGroups, setSalesGroups] = useState<ICategoryGroup[]>(null);
  const [error, setError] = useState<any>(null);
  const isGeneratingRules = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, CalculatorRules.GENERATE_CALCULATOR_RULES_REQUEST));
  const isGeneratingSalesGroups = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, CalculatorRules.GENERATE_SALES_GROUPS_REQUEST));
  const isUpdatingCalculatorRules = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, CalculatorRules.UPDATE_CALCULATOR_RULES_REQUEST));
  const currentCalculatorRules = useSelector(selectCalculatorRules());

  const switchType: SwitchType = switchState.has('rules') ? 'rules' : 'sales-groups';
  const sortedTipShareRules = rules?.rules.filter((rule) => !!rule.sharePolicy && !rule.default).sort(sortAlphaNumerically('contributor.name'));
  const defaultPoolRule = rules?.rules.find((rule) => rule.default);

  const handleError = (error: any) => {
    setError(error);
  };

  const handleSave = (type: SwitchType) => {
    switch (type) {
      // replace existing rules
      case 'rules': {
        dispatch(updateCalculatorRulesRequest(siteId, rules, { onSuccess: handleSaveSuccess, onError: handleError }, 'Calculator rules saved'));
        break;
      }

      // update sales groups only
      case 'sales-groups': {
        const newSalesGroupsNames = new Set(salesGroups.map((salesGroup) => salesGroup.name));
        const otherSalesGroups = currentCalculatorRules?.categoryGroups?.filter((salesGroup) => !newSalesGroupsNames.has(salesGroup.name)) ?? [];

        const newSalesGroups = [...salesGroups, ...otherSalesGroups];

        const newRules: ICreateUpdateCalculatorRules = {
          method: currentCalculatorRules?.method ?? TipCalculatorMethod.NONE,
          rules: currentCalculatorRules?.rules ?? [],
          participants: currentCalculatorRules?.participants,
          categoryGroups: newSalesGroups,
        };

        dispatch(updateCalculatorRulesRequest(siteId, newRules, { onSuccess: handleSaveSuccess, onError: handleError }, 'Sales groups saved'));
        break;
      }
    }
  };

  const handleSaveSuccess = () => {
    onClose();
  };

  const handleContinue = () => {
    switch (switchType) {
      case 'rules': {
        dispatch(generateCalculatorRulesRequest(siteId, (rules) => setRules(rules), handleError));
        break;
      }

      case 'sales-groups': {
        dispatch(generateSalesGroupsRequest(siteId, (salesGroups) => setSalesGroups(salesGroups), handleError));
        break;
      }
    }

    setModal('result');
  };

  const onChangeSwitchState = (type: SwitchType) => {
    setSwitchState((state) => {
      // toggle each switch,
      // but if sales groups switch is false, rules switch is set to false too
      switch (true) {
        case type === 'sales-groups' && !state.has('sales-groups'): {
          const newState = new Set(state);
          newState.add('sales-groups');
          return newState;
        }

        case type === 'sales-groups' && state.has('sales-groups'): {
          return new Set();
        }

        case type === 'rules' && !state.has('rules'): {
          return new Set<SwitchType>(['rules', 'sales-groups']);
        }

        case type === 'rules' && state.has('rules'): {
          const newState = new Set(state);
          newState.delete('rules');
          return newState;
        }

        default: {
          return state;
        }
      }
    });
  };

  return (
    <ModalFactory dataCy={dataCy} onClose={onClose} isActive maxWidth={500} closeOnOutSideClick={false}>
      <Content>
        {modal === 'generate' && (
          <>
            <HeaderContainer>
              <ModalTitle>Tip Calculator Sales Groups & Rules Generator</ModalTitle>

              <Text size="small" className="center">
                Select the items you would like to generate.
              </Text>
            </HeaderContainer>

            <SwitchContainer>
              <LabelContainer>
                <ItemLabel>Sales Groups</ItemLabel>
                <Switch data-cy="sales-groups-toggle" checked={switchState.has('sales-groups')} onChange={() => onChangeSwitchState('sales-groups')} />
              </LabelContainer>
              <ItemDescription>Generate sales groups from the sales and menu items.</ItemDescription>
            </SwitchContainer>

            <SwitchContainer>
              <LabelContainer>
                <ItemLabel>Tip Calculator Rules</ItemLabel>
                <Switch data-cy="rules-toggle" checked={switchState.has('rules')} onChange={() => onChangeSwitchState('rules')} />
              </LabelContainer>
              <ItemDescription>Generate rules for the tip calculator based on POS data.</ItemDescription>
            </SwitchContainer>

            <ContinueButton appearance="primary" disabled={switchState.size === 0} label="Continue" data-cy={`${dataCy}-continue-button`} onClick={() => handleContinue()} />
          </>
        )}

        {modal === 'result' && (isGeneratingRules || isGeneratingSalesGroups) && (
          <>
            <ModalTitle>Evaluating POS Data</ModalTitle>
            <StyledSpinner color={theme.color.secondary[400]} size={64} stroke={6} />
            <LoadingText>Please wait while we evaluate the POS data.</LoadingText>
          </>
        )}

        {modal === 'result' && !isGeneratingRules && rules && !error && (
          <>
            <HeaderContainer>
              <ModalTitle>Review Tip Calculator Rules & Sales Categories</ModalTitle>

              <Text size="small" className="center">
                The rules & sales categories below have been generated by reviewing your POS data. Please view before applying.
              </Text>
            </HeaderContainer>

            <ReviewHeader>Sales Groups</ReviewHeader>

            <SalesGroupsContainer data-cy="sales-groups-layout">
              {rules.categoryGroups.length === 0 && <DisclaimerText>Not enough POS data found to generate sales groups.</DisclaimerText>}
              {rules.categoryGroups.map((group, idx) => (
                <SalesGroupItem key={`sales-group-${group.name}_${idx}`} categoryGroup={group} disabled />
              ))}
            </SalesGroupsContainer>

            <ReviewHeader>Tip Calculator rules</ReviewHeader>

            <RulesLayout data-cy="rules-layout">
              {rules.rules.length === 0 ? (
                <DisclaimerText>Not enough POS data found to generate tip calculator rules.</DisclaimerText>
              ) : (
                <>
                  {sortedTipShareRules.map((rule, i) => (
                    <TipShareItem key={`tip-share-${i}`} tipShare={rule} disabled />
                  ))}
                  {defaultPoolRule && <TipShareItem tipShare={defaultPoolRule} disabled />}
                </>
              )}
            </RulesLayout>
            <ModalFooter columns={2}>
              <Button appearance="white" label="Cancel" data-cy={`${dataCy}-cancel-button`} onClick={() => onClose()} />
              <Button
                appearance="primary"
                label="Save"
                data-cy={`${dataCy}-save-button`}
                isProcessing={isUpdatingCalculatorRules}
                disabled={rules.rules?.length === 0 && rules.categoryGroups?.length === 0}
                onClick={() => handleSave('rules')}
              />
            </ModalFooter>
          </>
        )}

        {modal === 'result' && !isGeneratingSalesGroups && salesGroups && !error && (
          <>
            <HeaderContainer>
              <ModalTitle>Review Tip Calculator Sales Categories</ModalTitle>

              <Text size="small" className="center">
                The sales categories below have been generated by reviewing your POS data. Please view before applying.
              </Text>
            </HeaderContainer>

            <ReviewHeader>Sales Groups</ReviewHeader>

            <SalesGroupsContainer data-cy="sales-groups-layout">
              {salesGroups.length === 0 && <DisclaimerText>Not enough POS data found to generate sales groups.</DisclaimerText>}
              {salesGroups.map((group, idx) => (
                <SalesGroupItem key={`sales-group-${group.name}_${idx}`} categoryGroup={group} disabled />
              ))}
            </SalesGroupsContainer>

            <ModalFooter columns={2}>
              <Button appearance="white" label="Cancel" data-cy={`${dataCy}-cancel-button`} onClick={() => onClose()} />
              <Button appearance="primary" label="Save" data-cy={`${dataCy}-save-button`} disabled={salesGroups.length === 0} isProcessing={isUpdatingCalculatorRules} onClick={() => handleSave('sales-groups')} />
            </ModalFooter>
          </>
        )}

        {modal === 'result' && !isGeneratingRules && !isGeneratingSalesGroups && error && (
          <>
            <AlertHeader size={64} />
            <ModalTitle>We are having trouble</ModalTitle>
            <ErrorText>We encountered an issue generating the {switchType === 'rules' ? 'calculator rules' : 'sales groups'} for this site.</ErrorText>
            {error.errorMessages.length && <Message className="message" appearance={Appearance.WARNING} message={error.errorMessages[0]} />}
          </>
        )}
      </Content>
    </ModalFactory>
  );
};
